import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import MapPicker from "./MapPicker";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * LocationOnboardingModal
 *
 * Prompts new users to pin their location on the map.
 * Saves `city` (the Nominatim city/town level) and lat/lng to the profile.
 * Calls `onSaved({ city, locality })` so Browse can pre-filter listings.
 */
export default function LocationOnboardingModal({ isOpen, onClose, onSaved }) {
  const [location, setLocation] = useState({
    lat: null,
    lng: null,
    address: "",
    city: "",
    locality: "",
  });

  const handleSave = async () => {
    if (!location.lat || !location.lng) {
      toast.error("Please drop a pin on the map to select your location");
      return;
    }
    try {
      // Save the city not the suburb/neighbourhood
      await api.put("/profile", {
        city: location.city || "",
        latitude: location.lat,
        longitude: location.lng,
      });
      toast.success("Welcome! Your location has been set.");
      // Pass resolved city + locality back so Browse can filter immediately
      if (onSaved) onSaved({ city: location.city, locality: location.locality });
      onClose();
    } catch (e) {
      toast.error("Failed to save location");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-xl sm:rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-display font-medium text-stone-900">
            Where are you looking for a place?
          </DialogTitle>
          <DialogDescription className="text-stone-600 font-sans">
            Pin your location on the map so we can show you listings nearby.
          </DialogDescription>
        </DialogHeader>

        <div className="my-4">
          <MapPicker value={location} onChange={setLocation} />
          {location.address && (
            <p className="mt-3 text-xs text-stone-500 bg-stone-50 p-2.5 rounded-xl border border-stone-200 line-clamp-2">
              <span className="font-semibold text-stone-700">Selected: </span>
              {location.address}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            onClick={handleSave}
            className="brand-accent rounded-full w-full h-11 text-base"
          >
            Confirm Location &amp; Start Browsing
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
